import React, { useState, useEffect } from "react";
import { postRequest, getRequest, putRequest } from "../../apiWrapper";
import { toast } from "react-toastify";
import { FaStar, FaRegStar } from "react-icons/fa";
import { gradeOptions } from "../../constants/gradeOptions";

const statusOptionsMap = {
  task: [
    "not_started",
    "not_understood",
    "needs_explanation",
    "can_answer_alone",
  ],
  test: ["in_progress"]
};

const statusLabels = {
  not_started: "未着手",
  not_understood: "理解不十分",
  needs_explanation: "解説必要",
  can_answer_alone: "独力解答",
  mastered: "修得",
  in_progress: "練習中",
};

const AddStudentGoalModal = ({ onClose, onAdded, studentid, goalData, isAdmin = false }) => {
  const [subjects, setSubjects] = useState([]);
  const [form, setForm] = useState({
    title: "",
    subjectid: "",
    type: "task",
    priority: "medium",
    due_date: "",
    required_level: "",
    status: statusOptionsMap["task"][0],
    sub_type: null,
  });
  const [selectedGrades, setSelectedGrades] = useState([]);


  useEffect(() => {
    const fetchSubjects = async () => {
      try {
        const data = await getRequest("subjects");
        if (data?.subjects) setSubjects(data.subjects);
      } catch (error) {
        console.error("❌ 科目取得エラー:", error);
      }
    };
    fetchSubjects();

    // 既存データがある場合、フォームを初期化する
    if (goalData) {
      setForm({
        title: goalData.title,
        subjectid: goalData.subjectid,
        type: goalData.type,
        priority: goalData.priority,
        due_date: goalData.due_date || "",
        required_level: goalData.required_level || "",
        status: goalData.status || statusOptionsMap["task"][0]
      });
    }
  }, [goalData]);

  const handleChange = (field, value) => {
    setForm((prev) => {
      if (field === "type") {
        return {
          ...prev,
          type: value,
          status: statusOptionsMap[value][0]
        };
      }
      return { ...prev, [field]: value };
    });
  };

  const handleSubmit = async () => {
    if (!form.title || !form.subjectid) {
      toast.error("タイトルと科目は必須です");
      return;
    }

    // 一括登録
    if (form.sub_type === "bulk" && isAdmin) {
      if (selectedGrades.length === 0) {
        toast.error("少なくとも1つの学年を選択してください");
        return;
      }
    
      try {
        const studentids = [];
    
        for (const grade of selectedGrades) {
          const res = await getRequest(`students?grade=${grade}&excludeWithdrawn=true`);
          if (res?.students?.length) {
            studentids.push(...res.students.map(student => student.id));
          }
        }
    
        if (studentids.length === 0) {
          toast.error("該当する生徒が見つかりませんでした");
          return;
        }
    
        const res = await postRequest("student_goals/batch", {
          ...form,
          studentids,
          sub_type: "bulk",
        });
    
        const result = await res.json();
        if (res.ok) {
          const addedGoals = result.goals || [];
          const currentStudentGoal = addedGoals.find(goal => goal.studentid === studentid);
          if (currentStudentGoal) {
            onAdded(currentStudentGoal);
          }
          toast.success("一括登録が完了しました");
          onClose();
        } else {
          toast.error("一括登録に失敗しました");
        }
    
      } catch (error) {
        console.error("❌ 一括登録エラー:", error);
        toast.error("一括登録に失敗しました");
      }
    
      return;
    }    

    // 個別登録
    const payload = {
      ...form,
      studentid
    };

    try {
      let res;
      let result;

      if (goalData) {
        // 更新処理（PUTリクエスト）
        res = await putRequest(`student_goals/${goalData.id}`, payload);
        result = await res.json();
        if (res.ok) {
          toast.success("課題を保存しました");
        }
      } else {
        // 追加処理（POSTリクエスト）
        res = await postRequest("student_goals", payload);
        result = await res.json();
        if (res.ok) {
          toast.success("課題を追加しました");
        }
      }

      if (res.ok) {
        onAdded(result.goal);
        onClose();
      } else {
        toast.error("処理に失敗しました");
      }
    } catch (err) {
      console.error("❌ 課題処理エラー:", err);
      toast.error("処理に失敗しました");
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-40 flex items-center justify-center z-50">
      <div className="bg-white rounded-2xl p-6 w-full max-w-xl shadow-2xl space-y-6">
        <h2 className="text-2xl font-bold text-gray-800">{goalData ? "提案された課題を確認" : "課題を追加"}</h2>

        {/* タイトル */}
        <div>
          <label className="block text-sm font-semibold text-gray-700 mb-1">タイトル</label>
          <input
            type="text"
            className="w-full p-2.5 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-400"
            value={form.title}
            onChange={(e) => handleChange("title", e.target.value)}
          />
        </div>

        {/* 科目 */}
        <div>
          <label className="block text-sm font-semibold text-gray-700 mb-1">科目</label>
          <select
            className="w-full p-2.5 border border-gray-300 rounded-lg focus:outline-none"
            value={form.subjectid}
            onChange={(e) => handleChange("subjectid", e.target.value)}
          >
            <option value="">選択してください</option>
            {subjects.map((subj) => (
              <option key={subj.id} value={subj.id}>
                {subj.name}
              </option>
            ))}
          </select>
        </div>

        {/* 種類・優先度 */}
        <div className="flex gap-4 items-center">
          {/* 種類 */}
          <div className="flex-1">
            <label className="block text-sm font-semibold text-gray-700 mb-1">種類</label>
            <select
              className="w-full p-2.5 border border-gray-300 rounded-lg"
              value={form.type}
              onChange={(e) => handleChange("type", e.target.value)}
            >
              <option value="task">タスク</option>
              <option value="test">テスト</option>
            </select>
          </div>

          {/* 優先度 */}
          <div className="flex flex-col items-center justify-center">
            <label className="text-sm font-semibold text-gray-700 mb-1">優先</label>
            <button
              type="button"
              onClick={() =>
                handleChange("priority", form.priority === "high" ? "low" : "high")
              }
              className="text-xl"
              title={form.priority === "high" ? "優先" : "通常"}
            >
              {form.priority === "high" ? (
                <FaStar className="text-yellow-500" />
              ) : (
                <FaRegStar className="text-gray-400" />
              )}
            </button>
          </div>
        </div>

        {/* ステータス */}
        {form.type === "task" && (
          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-1">ステータス</label>
            <select
              className="w-full p-2.5 border border-gray-300 rounded-lg"
              value={form.status}
              onChange={(e) => handleChange("status", e.target.value)}
            >
              {statusOptionsMap[form.type].map((status) => (
                <option key={status} value={status}>
                  {statusLabels[status]}
                </option>
              ))}
            </select>
          </div>
        )}

        {/* 期限 */}
        <div>
          <label className="block text-sm font-semibold text-gray-700 mb-1">期限（任意）</label>
          <input
            type="date"
            className="w-full p-2.5 border border-gray-300 rounded-lg"
            value={form.due_date}
            onChange={(e) => handleChange("due_date", e.target.value)}
          />
        </div>

        {/* 合格ライン（testのみ表示） */}
        {form.type === "test" && (
          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-1">合格ライン（任意）</label>
            <input
              type="number"
              className="w-full p-2.5 border border-gray-300 rounded-lg"
              value={form.required_level}
              onChange={(e) => handleChange("required_level", e.target.value)}
              min="0"
              max="100"
              step="10"
            />
          </div>
        )}

        {isAdmin && (
          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-1">登録方法</label>
            <select
              className="w-full p-2.5 border border-gray-300 rounded-lg"
              value={form.sub_type}
              onChange={(e) => handleChange("sub_type", e.target.value)}
            >
              <option value="none">個別</option>
              <option value="bulk">一括（学年指定）</option>
            </select>
          </div>
        )}

        {isAdmin && form.sub_type === "bulk" && (
          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-1">対象学年</label>
            <div className="flex flex-wrap gap-2">
              {gradeOptions.map(({ value, label }) => (
                <button
                  key={value}
                  type="button"
                  onClick={() =>
                    setSelectedGrades((prev) =>
                      prev.includes(value) ? prev.filter((g) => g !== value) : [...prev, value]
                    )
                  }
                  className={`px-3 py-1 rounded-full border ${selectedGrades.includes(value) ? "bg-blue-500 text-white" : "bg-gray-100"
                    }`}
                >
                  {label}
                </button>
              ))}
            </div>
          </div>
        )}

        {/* ボタン */}
        <div className="flex justify-end gap-4 pt-4">
          {!goalData && (
            <button
              className="px-4 py-2 rounded-lg bg-gray-200 text-gray-700 hover:bg-gray-300 transition"
              onClick={onClose}
            >
              キャンセル
            </button>
          )}
          <button
            className="px-4 py-2 rounded-lg bg-blue-500 text-white hover:bg-blue-600 transition"
            onClick={handleSubmit}
          >
            {goalData ? "保存" : "追加"}
          </button>
        </div>
      </div>
    </div>
  );
};

export default AddStudentGoalModal;
